import React from 'react';
import { useGlobalFontSize } from '../../context/GlobalFontSizeContext';
import { useIsPhone } from '../hooks/useMediaQuery';

/**
 * Phone downscale for chart type (axis ticks, legends, in-card filter labels).
 *
 * The global control defaults to 16px and doubles as the portrait signage
 * "distance dial" (see docs/portrait-signage-standard.md), so it can't just be
 * lowered — 16px ticks are right on a wall panel and far too heavy on a 390px
 * phone. Charts therefore render at 75% of it below 480px, clamped so the
 * result stays legible at the bottom of the range and the control still has
 * some travel at the top (16 -> 12, 24 -> 13, 8 -> 8).
 */
const PHONE_FONT_SCALE = 0.75;
const PHONE_FONT_MIN = 8;
const PHONE_FONT_MAX = 13;

export const scaleChartFontSizeForPhone = (fontSize) =>
  Math.min(
    PHONE_FONT_MAX,
    Math.max(PHONE_FONT_MIN, Math.round(fontSize * PHONE_FONT_SCALE))
  );

/**
 * Chart Font Size Context
 * Wrapper that provides global font size to chart components
 * Uses the global font size context so charts respond to the top bar control
 */
export const ChartFontSizeProvider = ({ children }) => {
  // Just pass through children - charts will use useGlobalFontSize directly
  return <>{children}</>;
};

/**
 * Hook for chart components to get font size
 * Uses the global font size context
 */
export const useChartFontSize = () => {
  const { fontSize } = useGlobalFontSize();
  const isPhone = useIsPhone();
  // Return fontSize only (no increase/decrease since that's handled by top bar)
  return { fontSize: isPhone ? scaleChartFontSizeForPhone(fontSize) : fontSize };
};

export default ChartFontSizeProvider;
